// The translator of the free test pages (2026-11-04): plain functions, used
// by server components (TestPage, hub, metadata, share images) and, through
// components/i18n/I18nProvider.tsx, by the client test components.
//
// Messages are flat "namespace.key" strings from messages/<locale>/<ns>.json
// (lib/i18n/messages.ts). Syntax inside a message:
//   {name}            a value; numbers are formatted for the language
//                     (Latin digits, local separators);
//   key_one/_other... plural forms (Intl.PluralRules categories zero, one,
//                     two, few, many, other) picked by tp(key, count); {count}
//                     is the formatted count;
//   <b>…</b>          a tag for rich(): the caller maps each tag name to a
//                     React element (no nesting).
// A key missing in a language never reaches the page: lib/i18n/messages.ts
// checks every file against English at build time.
import type { ReactNode } from "react";
import { localeInfo, type Locale } from "./locales";

export type Messages = Record<string, string>;
export type Vars = Record<string, string | number | null | undefined>;

export interface Translator {
  locale: Locale;
  dir: "ltr" | "rtl";
  /** The message, with {values}. Throws for a key that doesn't exist (a code bug). */
  t: (key: string, vars?: Vars) => string;
  /** Plural message: key_one / key_few / … / key_other by `count`, {count} filled in. */
  tp: (key: string, count: number, vars?: Vars) => string;
  /** Message with <tag>…</tag> parts drawn by `tags`. */
  rich: (key: string, vars: Vars, tags: Record<string, (chunk: string) => ReactNode>) => ReactNode[];
  /** True when the key exists in this language's messages. */
  has: (key: string) => boolean;
  /** A number in the language's format (Latin digits). */
  num: (value: number, options?: Intl.NumberFormatOptions) => string;
  /** A date (YYYY-MM-DD) in the language's long format. */
  date: (iso: string) => string;
}

const PLACEHOLDER = /\{([a-zA-Z_][\w]*)\}/g;

// Number ranges with an en or em dash ("102–116", "18–24", "31–62%") in right-to-left text:
// the dash between two numbers would make the browser show them in reverse ("116–102").
// (A hyphen-minus between digits already stays in order: Unicode bidi rule W4.)
// A left-to-right isolate (U+2066 … U+2069, invisible) keeps the range in reading order.
// The same for counters ("3 / 10") and a number with a Latin unit ("500 ms", "+85 ms", "2.5 s"),
// and "60+" (age band), which would otherwise show as "10 / 3", "ms 500" and "+60".
const NUMBER_RANGE = /([+−-]?\d[\d.,]*%?\s?[–—/]\s?\d[\d.,]*%?(?:\s?(?:ms|s|mg)\b)?|[+−]?\d[\d.,]*\s?(?:ms|s|mg)\b|\d+\+)/g;

/** Wrap number ranges, counters and numbers with Latin units in a left-to-right isolate (right-to-left languages only). */
export function isolateRanges(text: string): string {
  // No-break spaces inside, so "100 ms" never splits across two lines.
  return text.replace(NUMBER_RANGE, (m) => `\u2066${m.replace(/ /g, "\u00a0")}\u2069`);
}

export function createTranslator(locale: Locale, messages: Messages): Translator {
  const info = localeInfo(locale);
  const numberFormats = new Map<string, Intl.NumberFormat>();
  const plurals = new Intl.PluralRules(info.intl);

  const num = (value: number, options?: Intl.NumberFormatOptions) => {
    const k = JSON.stringify(options ?? {});
    let f = numberFormats.get(k);
    if (!f) {
      f = new Intl.NumberFormat(info.intl, options);
      numberFormats.set(k, f);
    }
    return f.format(value);
  };

  const fill = (text: string, vars?: Vars) =>
    vars
      ? text.replace(PLACEHOLDER, (whole, name: string) => {
          const v = vars[name];
          if (v === undefined || v === null) return whole;
          return typeof v === "number" ? num(v) : v;
        })
      : text;

  const raw = (key: string): string => {
    const m = messages[key];
    if (m === undefined) throw new Error(`i18n: no message "${key}" for ${locale}`);
    return m;
  };

  const rtl = info.dir === "rtl";
  const t = (key: string, vars?: Vars) => (rtl ? isolateRanges(fill(raw(key), vars)) : fill(raw(key), vars));

  const pluralKey = (key: string, count: number) => {
    const cat = plurals.select(count);
    const k = `${key}_${cat}`;
    return messages[k] !== undefined ? k : `${key}_other`;
  };

  const tp = (key: string, count: number, vars?: Vars) => {
    const out = fill(raw(pluralKey(key, count)), { ...vars, count });
    return rtl ? isolateRanges(out) : out;
  };

  const rich = (key: string, vars: Vars, tags: Record<string, (chunk: string) => ReactNode>): ReactNode[] => {
    const text = rtl ? isolateRanges(fill(raw(key), vars)) : fill(raw(key), vars);
    const out: ReactNode[] = [];
    const re = /<([a-z]+)>([\s\S]*?)<\/\1>/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text))) {
      if (m.index > last) out.push(text.slice(last, m.index));
      const render = tags[m[1]];
      out.push(render ? render(m[2]) : m[2]);
      last = m.index + m[0].length;
    }
    if (last < text.length) out.push(text.slice(last));
    return out;
  };

  // English keeps the site's "3 November 2026" (lib/blog.ts formatDate).
  const dateFormat = new Intl.DateTimeFormat(locale === "en" ? "en-GB" : info.intl, { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
  const date = (iso: string) => dateFormat.format(new Date(`${iso}T00:00:00Z`));

  return { locale, dir: info.dir, t, tp, rich, has: (key) => messages[key] !== undefined, num, date };
}

/** The plural categories a language uses (for the message checks). */
export function pluralCategories(locale: Locale): string[] {
  return new Intl.PluralRules(localeInfo(locale).intl).resolvedOptions().pluralCategories as string[];
}

/** Placeholder names in a message ("{n} ms" -> ["n"]). */
export function placeholders(text: string): string[] {
  return [...text.matchAll(PLACEHOLDER)].map((m) => m[1]).sort();
}

/** Tag names in a message ("<b>x</b>" -> ["b"]). */
export function tagNames(text: string): string[] {
  return [...text.matchAll(/<([a-z]+)>/g)].map((m) => m[1]).sort();
}
